export const VIRTUAL_PAGEVIEW_EVENT = "virtual_pageview";
export const ADVERTISE_NOW_CLICK_EVENT = "advertise_now_click";
export const SCHEDULE_VISIT_EVENT = "schedule_visit";
export const SEARCH_EVENT = "search";
export const VIEW_ITEM_EVENT = "view_item";
export const ADD_TO_WISHLIST_EVENT = "add_to_wishlist";
export const REMOVE_FROM_WISHLIST_EVENT = "remove_from_wishlist";
export const LOGIN_EVENT = "login";
export const SIGN_UP_EVENT = "sign_up";
export const SCHEDULE_START_EVENT = "schedule_start";
export const CONTACT_CLICK_EVENT = "contact_click";
export const CURRENCY_BRL = "BRL";

export type AuthMethod = "email" | "facebook" | "google" | "phone";
export type ContactMethod = "whatsapp" | "phone";

export type ListingValue = {
  rentValue?: string | number | null;
  city?: string | null;
  uf?: string | null;
  neighborhood?: string | null;
};

export type TrackedUser = {
  id?: number | null;
  first_name?: string | null;
  email?: string | null;
  phone?: string | null;
};

type DataLayerPayload = Record<string, unknown>;

interface DataLayerHost {
  dataLayer?: DataLayerPayload[];
}

function getDataLayer(): DataLayerPayload[] {
  const host = window as Window & DataLayerHost;
  if (!Array.isArray(host.dataLayer)) {
    host.dataLayer = [];
  }
  return host.dataLayer;
}

export function pushDataLayer(payload: DataLayerPayload): void {
  try {
    getDataLayer().push(payload);
  } catch {
    // A missing or frozen dataLayer must not block navigation.
  }
}

export function pushVirtualPageView(page: {
  pagePath: string;
  pageLocation: string;
  pageTitle: string;
}): void {
  pushDataLayer({
    event: VIRTUAL_PAGEVIEW_EVENT,
    page_path: page.pagePath,
    page_location: page.pageLocation,
    page_title: page.pageTitle,
  });
}

function clientFields(user: TrackedUser) {
  return {
    user_id: user.id ?? null,
    email: user.email ?? null,
    phone: user.phone ?? null,
    first_name: user.first_name ?? null,
  };
}

function toMoney(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

const REAL_ESTATE_ITEM_CATEGORY = "real_estate";

function commerceFields(listing: {
  propertyId: string | number;
  title?: string | null;
} & ListingValue) {
  const value = toMoney(listing.rentValue);
  const itemName = listing.title ?? null;
  const itemId = String(listing.propertyId);
  return {
    content_ids: [itemId],
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        item_category: REAL_ESTATE_ITEM_CATEGORY,
        price: value,
      },
    ],
    item_name: itemName,
    value,
    currency: CURRENCY_BRL,
    city: listing.city ?? null,
    uf: listing.uf ?? null,
    neighborhood: listing.neighborhood ?? null,
  };
}

export function pushScheduleVisit(visit: {
  propertyId: string | number;
  scheduleId: number;
  visitDate: string;
  visitTime: string;
  videoCall: string | number | null;
  originSource: string | null;
  utmSource?: string | null;
  utmCampaign?: string | null;
  utmMedium?: string | null;
  utmContent?: string | null;
  title?: string | null;
  user: TrackedUser;
} & ListingValue): void {
  pushDataLayer({
    event: SCHEDULE_VISIT_EVENT,
    property_id: visit.propertyId,
    schedule_id: visit.scheduleId,
    visit_date: visit.visitDate,
    visit_time: visit.visitTime,
    video_call: visit.videoCall,
    origin_source: visit.originSource,
    utm_source: visit.utmSource ?? null,
    utm_campaign: visit.utmCampaign ?? null,
    utm_medium: visit.utmMedium ?? null,
    utm_content: visit.utmContent ?? null,
    ...commerceFields(visit),
    ...clientFields(visit.user),
  });
}

export function pushScheduleStart(intent: {
  propertyId: string | number;
  title?: string | null;
  rentValue?: string | number | null;
}): void {
  pushDataLayer({
    event: SCHEDULE_START_EVENT,
    property_id: intent.propertyId,
    ...commerceFields(intent),
  });
}

export function pushContactClick(contact: {
  method: ContactMethod;
  propertyId: string | number;
}): void {
  pushDataLayer({
    event: CONTACT_CLICK_EVENT,
    method: contact.method,
    property_id: contact.propertyId,
  });
}

export function pushSearch(query: {
  city?: string | null;
  uf?: string | null;
  neighborhood?: string | null;
}): void {
  pushDataLayer({
    event: SEARCH_EVENT,
    city: query.city ?? null,
    uf: query.uf ?? null,
    neighborhood: query.neighborhood ?? null,
  });
}

export function pushViewItem(item: {
  id: string | number;
  title?: string | null;
} & ListingValue): void {
  pushDataLayer({
    event: VIEW_ITEM_EVENT,
    property_id: item.id,
    ...commerceFields({ ...item, propertyId: item.id }),
  });
}

export function pushAddToWishlist(propertyId: number): void {
  pushDataLayer({
    event: ADD_TO_WISHLIST_EVENT,
    property_id: propertyId,
    ...commerceFields({ propertyId }),
  });
}

export function pushRemoveFromWishlist(propertyId: number): void {
  pushDataLayer({
    event: REMOVE_FROM_WISHLIST_EVENT,
    property_id: propertyId,
    ...commerceFields({ propertyId }),
  });
}

export function pushLogin(method: AuthMethod, user: TrackedUser): void {
  pushDataLayer({
    event: LOGIN_EVENT,
    method,
    ...clientFields(user),
  });
}

export function pushSignUp(method: "email" | "phone", user: TrackedUser): void {
  pushDataLayer({
    event: SIGN_UP_EVENT,
    method,
    ...clientFields(user),
  });
}
